import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import { isProduction, isStaging } from "@/utils/env";
import { sanitizeEmbeddedUrls, sanitizeUrl } from "@/utils/posthog-sanitize";

// Shared links (?token=), Bobbie/Control Center (hash) and login_hint=<email>
// put secrets and personal data in URLs: strip them from everything Sentry
// sends — the request URL, error messages and breadcrumbs.
const scrubEvent = (event) => {
  if (event.request) {
    if (event.request.url) event.request.url = sanitizeUrl(event.request.url);
    delete event.request.query_string;
    if (event.request.headers?.Referer) {
      event.request.headers.Referer = sanitizeUrl(
        event.request.headers.Referer,
      );
    }
  }
  if (event.message) event.message = sanitizeEmbeddedUrls(event.message);
  event.exception?.values?.forEach((exception) => {
    if (exception.value)
      exception.value = sanitizeEmbeddedUrls(exception.value);
  });
  // Transactions are named after the path (e.g. /reset-password/<code>/) and
  // their spans describe requests ("GET https://…?token=…"); stack frames
  // carry the page URL too.
  if (event.transaction) event.transaction = sanitizeUrl(event.transaction);
  if (event.tags?.url) event.tags.url = sanitizeUrl(event.tags.url);
  event.spans?.forEach((span) => {
    if (span.description) {
      span.description = sanitizeEmbeddedUrls(span.description);
    }
    ["url", "http.url"].forEach((key) => {
      if (typeof span.data?.[key] === "string") {
        span.data[key] = sanitizeUrl(span.data[key]);
      }
    });
  });
  event.exception?.values?.forEach((exception) => {
    exception.stacktrace?.frames?.forEach((frame) => {
      // @sentry/browser 7 puts the URL in filename; abs_path for other SDKs.
      if (frame.filename) frame.filename = sanitizeUrl(frame.filename);
      if (frame.abs_path) frame.abs_path = sanitizeUrl(frame.abs_path);
    });
  });
  return event;
};

const scrubBreadcrumb = (breadcrumb) => {
  const data = breadcrumb.data;
  if (data) {
    ["url", "from", "to"].forEach((key) => {
      if (typeof data[key] === "string") data[key] = sanitizeUrl(data[key]);
    });
    // Console breadcrumbs keep the raw console arguments (URLs, request
    // objects); the joined text is already in the (scrubbed) message.
    delete data.arguments;
  }
  if (breadcrumb.message) {
    breadcrumb.message = sanitizeEmbeddedUrls(breadcrumb.message);
  }
  return breadcrumb;
};

const initializeSentry = (app) => {
  const sentryEnvironment = isStaging
    ? "Staging"
    : isProduction
    ? "Production"
    : null;
  if (sentryEnvironment === null) {
    throw new Error("Couldn't initialize Sentry environment");
  }
  Sentry.init({
    app,
    dsn: "https://4a4aa0c69ba3bf19c8185aafd3689e1d@o540004.ingest.us.sentry.io/4506772870135808",
    environment: sentryEnvironment,
    // No session replay: it records user behaviour without consent.
    integrations: [new Integrations.BrowserTracing()],
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    tracesSampleRate: 1.0,
    logErrors: true,
    // Vue component props hold the project (address, coordinates): don't
    // attach them to error reports.
    attachProps: false,
  });
};

export { initializeSentry, scrubBreadcrumb, scrubEvent };
